import { useState } from 'react';
import { INTERNSHIPS_DATA } from '@lib/constants';
import Icon from '@components/common/Icon';

const InternshipCardsSection = () => {
  const [expandedCard, setExpandedCard] = useState(null);

  const toggleCard = (id) => {
    setExpandedCard(expandedCard === id ? null : id);
  };

  return (
    <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8">
      <div className="container mx-auto max-w-7xl">
        {/* Section Header */}
        <div className="text-center mb-12 space-y-4">
          <h2
            className="text-4xl sm:text-5xl font-heading font-bold"
            style={{ color: 'var(--color-heading)' }}
          >
            Professional Experience
          </h2>
          <p
            className="text-lg max-w-3xl mx-auto"
            style={{ color: 'var(--color-subtle)' }}
          >
            Diverse experiences in education, retail technology, artificial intelligence, and industrial automation
          </p>
        </div>

        {/* Internship Cards - Horizontal scroll on mobile, grid on desktop */}
        <div>
          {/* Mobile: Horizontal scroll */}
          <div className="md:hidden flex gap-6 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide">
            {INTERNSHIPS_DATA.map((internship) => {
              const isExpanded = expandedCard === internship.id;

              return (
                <div
                  key={internship.id}
                  className="rounded-xl transition-all duration-300 hover:shadow-card-hover cursor-pointer min-w-[320px] flex-shrink-0 snap-center"
                  style={{
                    backgroundColor: 'var(--color-card-bg)',
                    borderColor: isExpanded ? 'var(--color-accent-6)' : 'var(--color-card-border)',
                    borderWidth: '1px',
                    borderStyle: 'solid'
                  }}
                  onClick={() => toggleCard(internship.id)}
                >
                <div className="p-6 space-y-4">
                  {/* Company Icon and Name */}
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      <div
                        className="w-12 h-12 rounded-lg flex items-center justify-center"
                        style={{
                          backgroundColor: 'var(--color-theme-2)',
                          borderColor: 'var(--color-border-light)',
                          borderWidth: '1px',
                          borderStyle: 'solid'
                        }}
                      >
                        <Icon name={internship.icon} size="2xl" className="text-4xl" style={{ color: 'var(--color-accent-6)' }} />
                      </div>
                      <div>
                        <h3
                          className="font-semibold text-lg"
                          style={{ color: 'var(--color-heading)' }}
                        >
                          {internship.company}
                        </h3>
                        <p
                          className="text-sm"
                          style={{ color: 'var(--color-muted)' }}
                        >
                          {internship.location}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Role and Period */}
                  <div>
                    <h4
                      className="font-semibold"
                      style={{ color: 'var(--color-subheading)' }}
                    >
                      {internship.role}
                    </h4>
                    <p
                      className="text-sm mt-1"
                      style={{ color: 'var(--color-muted)' }}
                    >
                      {internship.period} • {internship.duration}
                    </p>
                  </div>

                  {/* Description */}
                  <p
                    className="text-sm leading-relaxed"
                    style={{ color: 'var(--color-subtle)' }}
                  >
                    {internship.description}
                  </p>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="mt-6 pt-6 space-y-6 border-t" style={{ borderColor: 'var(--color-border-light)' }}>
                      {/* Key Achievements */}
                      <div>
                        <h5
                          className="font-semibold text-sm mb-3"
                          style={{ color: 'var(--color-heading)' }}
                        >
                          Key Achievements
                        </h5>
                        <ul className="space-y-2">
                          {internship.achievements.map((achievement, idx) => (
                            <li
                              key={idx}
                              className="text-sm flex items-start"
                              style={{ color: 'var(--color-subtle)' }}
                            >
                              <span
                                className="mr-2 mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0"
                                style={{ backgroundColor: 'var(--color-accent-6)' }}
                              ></span>
                              <span>{achievement}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Skills */}
                      <div>
                        <h5
                          className="font-semibold text-sm mb-3"
                          style={{ color: 'var(--color-heading)' }}
                        >
                          Skills Acquired
                        </h5>
                        <div className="flex flex-wrap gap-2">
                          {internship.skills.map((skill, idx) => (
                            <span
                              key={idx}
                              className="px-3 py-1 text-xs rounded-full font-medium"
                              style={{
                                backgroundColor: 'var(--color-theme-2)',
                                color: 'var(--color-accent-6)',
                                borderColor: 'var(--color-border-light)',
                                borderWidth: '1px',
                                borderStyle: 'solid'
                              }}
                            >
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* View Certificate Button */}
                      <div className="flex gap-3">
                        <a
                          href={internship.certificate}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="flex-1 text-center py-2 px-4 rounded-lg text-sm font-medium transition-all duration-200"
                          style={{
                            backgroundColor: 'var(--color-button-primary-bg)',
                            color: 'var(--color-button-primary-text)'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--color-button-primary-hover)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--color-button-primary-bg)';
                          }}
                        >
                          View Certificate
                        </a>
                        {internship.projectDoc && (
                          <a
                            href={internship.projectDoc}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="flex-1 text-center py-2 px-4 rounded-lg text-sm font-medium transition-all duration-200"
                            style={{
                              borderColor: 'var(--color-button-outline-border)',
                              color: 'var(--color-button-outline-text)',
                              borderWidth: '1px',
                              borderStyle: 'solid',
                              backgroundColor: 'transparent'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = 'var(--color-button-outline-hover-bg)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            Project Docs
                          </a>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Click to expand indicator */}
                  <div className="text-center pt-2">
                    <span
                      className="text-xs font-medium"
                      style={{ color: 'var(--color-accent-6)' }}
                    >
                      {isExpanded ? 'Click to collapse' : 'Click to expand'}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
          </div>

          {/* Desktop: Grid */}
          <div className="hidden md:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {INTERNSHIPS_DATA.map((internship) => {
              const isExpanded = expandedCard === internship.id;

              return (
                <div
                  key={internship.id}
                  className="rounded-xl transition-all duration-300 hover:shadow-card-hover cursor-pointer"
                  style={{
                    backgroundColor: 'var(--color-card-bg)',
                    borderColor: isExpanded ? 'var(--color-accent-6)' : 'var(--color-card-border)',
                    borderWidth: '1px',
                    borderStyle: 'solid'
                  }}
                  onClick={() => toggleCard(internship.id)}
                >
                <div className="p-6 space-y-4">
                  {/* Company Icon and Name */}
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      <div
                        className="w-12 h-12 rounded-lg flex items-center justify-center"
                        style={{
                          backgroundColor: 'var(--color-theme-2)',
                          borderColor: 'var(--color-border-light)',
                          borderWidth: '1px',
                          borderStyle: 'solid'
                        }}
                      >
                        <Icon name={internship.icon} size="2xl" className="text-4xl" style={{ color: 'var(--color-accent-6)' }} />
                      </div>
                      <div>
                        <h3
                          className="font-semibold text-lg"
                          style={{ color: 'var(--color-heading)' }}
                        >
                          {internship.company}
                        </h3>
                        <p
                          className="text-sm"
                          style={{ color: 'var(--color-muted)' }}
                        >
                          {internship.location}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Role and Period */}
                  <div>
                    <h4
                      className="font-semibold"
                      style={{ color: 'var(--color-subheading)' }}
                    >
                      {internship.role}
                    </h4>
                    <p
                      className="text-sm mt-1"
                      style={{ color: 'var(--color-muted)' }}
                    >
                      {internship.period} • {internship.duration}
                    </p>
                  </div>

                  {/* Description */}
                  <p
                    className="text-sm leading-relaxed"
                    style={{ color: 'var(--color-subtle)' }}
                  >
                    {internship.description}
                  </p>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="mt-6 pt-6 space-y-6 border-t" style={{ borderColor: 'var(--color-border-light)' }}>
                      {/* Key Achievements */}
                      <div>
                        <h5
                          className="font-semibold text-sm mb-3"
                          style={{ color: 'var(--color-heading)' }}
                        >
                          Key Achievements
                        </h5>
                        <ul className="space-y-2">
                          {internship.achievements.map((achievement, idx) => (
                            <li
                              key={idx}
                              className="text-sm flex items-start"
                              style={{ color: 'var(--color-subtle)' }}
                            >
                              <span
                                className="mr-2 mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0"
                                style={{ backgroundColor: 'var(--color-accent-6)' }}
                              ></span>
                              <span>{achievement}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Skills */}
                      <div>
                        <h5
                          className="font-semibold text-sm mb-3"
                          style={{ color: 'var(--color-heading)' }}
                        >
                          Skills Acquired
                        </h5>
                        <div className="flex flex-wrap gap-2">
                          {internship.skills.map((skill, idx) => (
                            <span
                              key={idx}
                              className="px-3 py-1 text-xs rounded-full font-medium"
                              style={{
                                backgroundColor: 'var(--color-theme-2)',
                                color: 'var(--color-accent-6)',
                                borderColor: 'var(--color-border-light)',
                                borderWidth: '1px',
                                borderStyle: 'solid'
                              }}
                            >
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* View Certificate Button */}
                      <div className="flex gap-3">
                        <a
                          href={internship.certificate}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="flex-1 text-center py-2 px-4 rounded-lg text-sm font-medium transition-all duration-200"
                          style={{
                            backgroundColor: 'var(--color-button-primary-bg)',
                            color: 'var(--color-button-primary-text)'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--color-button-primary-hover)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'var(--color-button-primary-bg)';
                          }}
                        >
                          View Certificate
                        </a>
                        {internship.projectDoc && (
                          <a
                            href={internship.projectDoc}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="flex-1 text-center py-2 px-4 rounded-lg text-sm font-medium transition-all duration-200"
                            style={{
                              borderColor: 'var(--color-button-outline-border)',
                              color: 'var(--color-button-outline-text)',
                              borderWidth: '1px',
                              borderStyle: 'solid',
                              backgroundColor: 'transparent'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = 'var(--color-button-outline-hover-bg)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            Project Docs
                          </a>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Click to expand indicator */}
                  <div className="text-center pt-2">
                    <span
                      className="text-xs font-medium"
                      style={{ color: 'var(--color-accent-6)' }}
                    >
                      {isExpanded ? 'Click to collapse' : 'Click to expand'}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default InternshipCardsSection;
